/**
 * RosterAdminView v2 — port of the locked roster table, READ-ONLY display of the
 * live jobs/{jobId}/rosterEntries, plus an "Import roster" action that REUSES the
 * existing import path (the live RosterImportView → ingest.importRoster). No new
 * write logic here.
 */
import React, { useMemo, useState } from "react";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import RosterImportView from "../RosterImportView.jsx";

const initials = (name) => (name ? name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") : "·");
const nameOf = (r) => (r.name || r.displayName || r.displayId || "—");

const CAP_FILTERS = [["all", "All"], ["captured", "Captured"], ["not", "Not yet"]];

// canImport false: no Import roster button (an event account, docs/ops/
// event-role.md; the import is admin-only on the server anyway).
export default function RosterAdminView({ jobId, jobLabel, canImport = true }) {
  const { data: roster, loading } = useFirestoreCollection(jobId ? `jobs/${jobId}/rosterEntries` : null);
  const [search, setSearch] = useState("");
  const [capFilter, setCapFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [importing, setImporting] = useState(false);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...roster]
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
      .filter((r) => (capFilter === "captured" ? !!r.captured : capFilter === "not" ? !r.captured : true))
      .filter((r) => {
        if (!q) return true;
        const cf = r.customFields || {}, pf = r.personFields || {};
        return [r.name, r.displayId, r.subjectType, pf.email, pf.phone, cf.role, cf.union, cf.edbrStatus]
          .filter(Boolean).map((v) => String(v).toLowerCase()).join(" ").includes(q);
      });
  }, [roster, search, capFilter]);

  const capturedCount = useMemo(() => roster.filter((r) => r.captured).length, [roster]);
  const selected = selectedId ? roster.find((r) => r.id === selectedId) : null;

  return (
    <div>
      <div className="rigv2-roster-head">
        <h1 className="rigv2-roster-title">Roster</h1>
        <span className="rigv2-roster-sub">{roster.length} entries</span>
        <span className="rigv2-roster-tally"><strong>{capturedCount}</strong> captured · <strong>{roster.length - capturedCount}</strong> not yet</span>
      </div>

      <div className="rigv2-roster-toolbar">
        <span className="rigv2-gal-search" style={{ flex: 1, minWidth: 220 }}>
          <input placeholder="Search name, role, union, phone, email, EDBR…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </span>
        {CAP_FILTERS.map(([id, label]) => (
          <button key={id} type="button" className={`rigv2-chip ${capFilter === id ? "on" : ""}`} onClick={() => setCapFilter(id)}>{label}</button>
        ))}
        {canImport && <button type="button" className="rigv2-roster-import" onClick={() => setImporting(true)}>Import roster</button>}
      </div>

      {loading ? (
        <div className="rigv2-empty">Loading roster…</div>
      ) : rows.length === 0 ? (
        <div className="rigv2-empty">{roster.length === 0 ? (canImport ? "No roster yet — import a producer CSV." : "No roster yet.") : "No entries match."}</div>
      ) : (
        <div className="rigv2-roster-list">
          {rows.map((r) => {
            const cf = r.customFields || {};
            const decline = String(cf.edbrStatus || "").toLowerCase() === "decline";
            return (
              <button key={r.id} type="button" className={`rigv2-rrow ${selectedId === r.id ? "sel" : ""}`} onClick={() => setSelectedId(selectedId === r.id ? null : r.id)}>
                <span className="rigv2-roster-av">{initials(r.name)}</span>
                <span className="rigv2-rrow-main">
                  <span className="rigv2-rrow-name">{nameOf(r)}</span>
                  <span className="rigv2-rrow-meta">{[r.subjectType, cf.role, cf.union].filter(Boolean).join(" · ") || "—"}</span>
                </span>
                <span className="rigv2-rrow-right">
                  {cf.kind && <span className="rigv2-kind-tag">{cf.kind}</span>}
                  {cf.edbrStatus && <span className={`rigv2-edbr ${decline ? "decline" : ""}`}>{cf.edbrStatus}</span>}
                  <span className={`rigv2-rrow-cap ${r.captured ? "yes" : "no"}`}>{r.captured ? "Captured" : "—"}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {selected && <RosterDetail entry={selected} />}

      {canImport && importing && (
        <RosterImportView
          jobId={jobId}
          jobLabel={jobLabel ?? jobId}
          onClose={() => setImporting(false)}
          onImported={() => setImporting(false)}
        />
      )}
    </div>
  );
}

function RosterDetail({ entry }) {
  const cf = entry.customFields || {}, pf = entry.personFields || {};
  const Row = ({ k, v }) => (v ? <div className="rigv2-detail-row"><span className="k">{k}</span><span>{v}</span></div> : null);
  return (
    <div className="rigv2-detail">
      <Row k="Name" v={nameOf(entry)} />
      <Row k="ID" v={entry.displayId} />
      <Row k="Label" v={cf.kind} />
      <Row k="Type" v={entry.subjectType} />
      <Row k="Role" v={cf.role} />
      <Row k="Union" v={[cf.union, cf.unionId].filter(Boolean).join(" · ")} />
      <Row k="Email" v={pf.email} />
      <Row k="Phone" v={pf.phone} />
      <Row k="EDBR" v={cf.edbrStatus} />
      <Row k="Captured" v={entry.captured ? "Yes" : "No"} />
    </div>
  );
}
